import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth-context';
import { money, percent } from '../lib/format';
import { isReadOnly } from '../lib/permissions';
import { toast } from '../lib/toast';
import {
  crossRuleViolation,
  HARDWARE_DISPOSITION_LABELS,
  HARDWARE_DISPOSITIONS,
  ITEM_SECONDARY_LANGUAGES,
  ITEM_TEXT_LANGUAGE_LABELS,
  ITEM_UNITS,
  itemErrorMessage,
  itemFieldProblems,
  MONEY_PATTERN,
  MONTHS_PATTERN,
  SUPPLY_KIND_LABELS,
  SUPPLY_KINDS,
  toastItemError,
  trimRateText,
  UNIT_LABELS,
  useCreateItem,
  useDeleteItem,
  useItems,
  useUpdateItem,
  VAT_RATE_CHOICES,
  type CreateItemBody,
  type HardwareDisposition,
  type Item,
  type ItemSecondaryLanguage,
  type ItemText,
  type ItemWriteBody,
  type ItemUnit,
  type SupplyKind,
} from '../lib/items-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';

/**
 * Katalog (mal ve hizmet).
 *
 * ⚠ BU EKRANIN ASIL İŞİ DÖRT ALANI DOĞRU ANLATMAKTIR: `supplyKind`, `isEss`,
 * `isImmovableWork`, `hardwareDisposition`. Bu dördü KDV çözümleyicisinin ÜRÜN
 * tarafı girdisidir ve yanlış değer YANLIŞ INTERVAT KUTUSU üretir. Hata anında
 * görünmez — üç ay sonra IC listing reddedildiğinde, ya da hiç reddedilmeyip
 * EKSİK BEYAN olarak kaldığında, yani denetimde ortaya çıkar. Bu yüzden her
 * alanın altında tanımı değil SONUCU yazıyoruz: hangi kutuya düşer, hangi IC
 * listing kodunu alır.
 *
 * ⚠ Para ve oran METİN olarak taşınır. Girdi kutusundan çıkan ham dize
 * doğrudan gövdeye konur; panelde `Number()`/`parseFloat` YOKTUR. Sebep basit:
 * `(1.005).toFixed(2) === '1.00'` — kuruş, değer bir kez double'a çevrildiği
 * anda ve tam sınırda kaybolur.
 */

const PAGE_SIZE = 25;

type TriFilter = 'all' | 'true' | 'false';

function triToBoolean(value: TriFilter): boolean | undefined {
  return value === 'all' ? undefined : value === 'true';
}

export function ItemsPage() {
  const { user } = useAuth();
  const readOnly = isReadOnly(user);

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<TriFilter>('true');
  const [essFilter, setEssFilter] = useState<TriFilter>('all');
  const [supplyFilter, setSupplyFilter] = useState<SupplyKind | 'all'>('all');
  const [offset, setOffset] = useState(0);

  const [editing, setEditing] = useState<Item | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Item | null>(null);

  // Her tuşta istek atmamak için: arama kutusu yazma hızında, sorgu 300 ms
  // sonra hareket eder.
  const debouncedSearch = useDebounced(search.trim(), 300);

  const params = useMemo(
    () => ({
      ...(debouncedSearch !== '' ? { q: debouncedSearch } : {}),
      ...(triToBoolean(activeFilter) !== undefined ? { active: triToBoolean(activeFilter) } : {}),
      ...(triToBoolean(essFilter) !== undefined ? { isEss: triToBoolean(essFilter) } : {}),
      ...(supplyFilter !== 'all' ? { supplyKind: supplyFilter } : {}),
      limit: PAGE_SIZE,
      offset,
    }),
    [debouncedSearch, activeFilter, essFilter, supplyFilter, offset],
  );

  const list = useItems(params);
  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  /** Süzgeç değişince sayfa başa döner: aksi hâlde 3. sayfada boş ekran kalır. */
  function resetPaging<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setOffset(0);
    };
  }

  return (
    <>
      <PageHeader
        title="Katalog"
        description="Fatura satırları yalnızca katalog kalemiyle açılır. Kalemdeki dört KDV alanı, faturanın hangi Intervat kutusuna düşeceğini belirler."
        actions={
          readOnly ? undefined : <Button onClick={() => setCreating(true)}>Yeni kalem</Button>
        }
      />

      <Card className="mb-4">
        <div className="grid gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            label="Ara"
            placeholder="Kod veya ad (tüm diller)"
            value={search}
            onChange={(e) => resetPaging(setSearch)(e.target.value)}
          />
          <Select
            label="Durum"
            value={activeFilter}
            onChange={(e) => resetPaging(setActiveFilter)(e.target.value as TriFilter)}
          >
            <option value="true">Aktif</option>
            <option value="false">Pasif</option>
            <option value="all">Tümü</option>
          </Select>
          <Select
            label="Tür"
            value={supplyFilter}
            onChange={(e) => resetPaging(setSupplyFilter)(e.target.value as SupplyKind | 'all')}
          >
            <option value="all">Tümü</option>
            {SUPPLY_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {SUPPLY_KIND_LABELS[kind]}
              </option>
            ))}
          </Select>
          <Select
            label="Elektronik hizmet"
            value={essFilter}
            onChange={(e) => resetPaging(setEssFilter)(e.target.value as TriFilter)}
          >
            <option value="all">Tümü</option>
            <option value="true">Evet (OSS'ye girebilir)</option>
            <option value="false">Hayır</option>
          </Select>
        </div>
      </Card>

      <Card>
        {list.isPending ? (
          <div className="px-4 py-10 text-center">
            <Spinner size={20} />
          </div>
        ) : list.isError ? (
          <ErrorState message={itemErrorMessage(list.error)} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Kalem yok"
            description={
              debouncedSearch !== '' ||
              activeFilter !== 'true' ||
              essFilter !== 'all' ||
              supplyFilter !== 'all'
                ? 'Süzgeçlere uyan kalem bulunamadı.'
                : 'Tek seferlik işler için de katalogda genel bir kalem açın ("Prestation de services") — serbest metin fatura satırı yoktur.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Kod</Th>
                  <Th>Ad</Th>
                  <Th>KDV nitelendirmesi</Th>
                  <Th>Birim</Th>
                  <Th align="right">Birim fiyat</Th>
                  <Th align="right">KDV</Th>
                  <Th align="right">İşlem</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.id} className={item.active ? undefined : 'opacity-60'}>
                    <Td className="font-mono text-xs">{item.code ?? '—'}</Td>
                    <Td>
                      <div className="font-medium text-[var(--c-ink)]">{item.name.fr}</div>
                      {item.name.tr && (
                        <div className="text-xs text-[var(--c-ink-faint)]">{item.name.tr}</div>
                      )}
                      {!item.active && (
                        <div className="mt-1">
                          <Badge tone="neutral">pasif</Badge>
                        </div>
                      )}
                    </Td>
                    <Td>
                      <QualificationBadges item={item} />
                    </Td>
                    <Td>{UNIT_LABELS[item.unit]}</Td>
                    <Td align="right">{money(item.unitPrice)}</Td>
                    <Td align="right">{percent(item.vatRate)}</Td>
                    <Td align="right">
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => setEditing(item)}>
                          {readOnly ? 'Görüntüle' : 'Düzenle'}
                        </Button>
                        {!readOnly && (
                          <Button variant="ghost" size="sm" onClick={() => setDeleting(item)}>
                            Sil
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <footer className="flex items-center justify-between gap-3 px-4 py-3 text-xs text-[var(--c-ink-soft)]">
              <span>
                {offset + 1}–{offset + rows.length} / {total}
                {list.isFetching && <span className="ml-2 opacity-70">yenileniyor…</span>}
              </span>
              <span className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Önceki
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset + rows.length >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Sonraki
                </Button>
              </span>
            </footer>
          </>
        )}
      </Card>

      <Legend />

      {creating && <ItemFormModal mode="create" onClose={() => setCreating(false)} />}

      {editing && (
        <ItemFormModal
          mode={readOnly ? 'view' : 'edit'}
          item={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && <DeleteItemModal item={deleting} onClose={() => setDeleting(null)} />}
    </>
  );
}

// ──────────────────────────────────────────────────────────── liste parçaları

function QualificationBadges({ item }: { item: Item }) {
  return (
    <div className="flex flex-wrap gap-1">
      <span title={supplyKindConsequence(item.supplyKind)}>
        <Badge tone={item.supplyKind === 'SERVICE' ? 'brand' : 'neutral'}>
          {SUPPLY_KIND_LABELS[item.supplyKind]}
        </Badge>
      </span>
      {item.isEss && (
        <span title="Elektronik hizmet: 10.000 € eşiği aşıldıysa ya da OSS'ye geçildiyse AB tüketicisinde varış ülkesi oranı ve OSS (kutu 47); eşiğin altında Belçika %21.">
          <Badge tone="warn">ESS</Badge>
        </span>
      )}
      {item.isImmovableWork && (
        <span title="Taşınmaz üzerinde iş: Belçikalı, periyodik beyan veren B2B müşteride yurtiçi ters yükümlülük (kutu 45).">
          <Badge tone="warn">Taşınmaz işi</Badge>
        </span>
      )}
      {item.hardwareDisposition !== 'NA' && (
        <span title={hardwareConsequence(item.hardwareDisposition)}>
          <Badge tone="neutral">
            {item.hardwareDisposition === 'SOLD' ? 'Donanım satılıyor' : 'Donanım kiralanıyor'}
          </Badge>
        </span>
      )}
    </div>
  );
}

function Legend() {
  return (
    <Card className="mt-4">
      <CardHeader
        title="Rozetler ne anlatıyor?"
        description="Kutu numarası tek başına kalemden çıkmaz: müşterinin ülkesi ve statüsüyle BİRLİKTE belirlenir. Buradaki alanlar o kararın ürün tarafı girdisidir."
      />
      <dl className="grid gap-3 px-4 py-3 text-xs text-[var(--c-ink-soft)] sm:grid-cols-2">
        <div>
          <dt className="font-medium text-[var(--c-ink)]">Hizmet / Mal</dt>
          <dd>
            AB'deki B2B müşteride: Hizmet → ters yükümlülük, <b>kutu 44</b>, IC listing kodu{' '}
            <b>S</b>. Mal → art. 39bis teslimi, <b>kutu 46</b>, kod <b>L</b> — ancak “Donanımın
            akıbeti” <b>satıldı</b> ise; “donanım yok” bırakılan bir mal kalemi o satışta bloke
            olur.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-[var(--c-ink)]">ESS (elektronik hizmet)</dt>
          <dd>
            AB tüketicisinde varış ülkesi oranı ve OSS (<b>kutu 47</b>) yalnızca AB geneli B2C
            cironuz <b>10.000 €</b> eşiğini aştıysa ya da OSS'ye gönüllü geçtiyseniz (Dir. md. 59c).
            Eşiğin altında — ve M1'in varsayılanında — aynı müşteriye Belçika %21'i uygulanır.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-[var(--c-ink)]">Taşınmaz işi</dt>
          <dd>
            Belçikalı, periyodik beyan veren B2B müşteride yurtiçi ters yükümlülük: kutu 3/54 yerine{' '}
            <b>kutu 45</b>, faturada KDV gösterilmez.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-[var(--c-ink)]">Donanım: satıldı / kiralandı</dt>
          <dd>
            Aynı kiosk, sözleşmeye göre iki ayrı kutuya gider: satıldı → mal (<b>46</b>, kod{' '}
            <b>L</b>), kiralandı → hizmet (<b>44</b>, kod <b>S</b>).
          </dd>
        </div>
      </dl>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────── form: yardımcılar

type FormMode = 'create' | 'edit' | 'view';

type LanguageValues = Record<'fr' | ItemSecondaryLanguage, string>;

function toLanguageValues(text: ItemText | null): LanguageValues {
  return {
    fr: text?.fr ?? '',
    nl: text?.nl ?? '',
    en: text?.en ?? '',
    tr: text?.tr ?? '',
  };
}

/**
 * Form değerlerinden jsonb gövdesi.
 *
 * ⚠ BOŞ BIRAKILAN DİL GÖVDEYE HİÇ KONMAZ. Boş dize göndermek sunucuda
 * `@MinLength(1)`e takılır (400) — "bu dili sil" demenin yolu anahtarı
 * göndermemektir; güncellemede `name` bütünüyle yerine geçtiği için bu, o dili
 * gerçekten temizler.
 */
function buildItemText(values: LanguageValues): ItemText | null {
  const fr = values.fr.trim();
  if (fr === '') return null;

  const extras: Partial<Record<ItemSecondaryLanguage, string>> = {};
  for (const lang of ITEM_SECONDARY_LANGUAGES) {
    const value = values[lang].trim();
    if (value !== '') extras[lang] = value;
  }
  return { fr, ...extras };
}

function hasAnyText(values: LanguageValues): boolean {
  return (
    values.fr.trim() !== '' || ITEM_SECONDARY_LANGUAGES.some((lang) => values[lang].trim() !== '')
  );
}

function supplyKindConsequence(kind: SupplyKind): string {
  if (kind === 'SERVICE') {
    return "AB'deki B2B müşteride ters yükümlülük → kutu 44, IC listing kodu S.";
  }
  /*
   * ⚠ MAL kutusu KOŞULLUDUR. Çözümleyici kutu 46'ya yalnız
   * `hardwareDisposition === 'SOLD'` iken gider; "Donanım yok" (varsayılan)
   * bırakılmış bir MAL kalemi AB B2B satışında BLOKE olur
   * (SUPPLY_KIND_DISPOSITION_MISMATCH). Koşulsuz "kutu 46" yazmak, operatöre
   * kaydettiği kalemin çalışacağını söylemek olurdu.
   */
  return (
    "AB'deki B2B müşteride art. 39bis teslimi → kutu 46, IC listing kodu L — ANCAK " +
    '"Donanımın akıbeti" SATILDI ise. "Donanım yok" bırakılan bir MAL kalemi o satışta ' +
    'bloke olur: mülkiyet devredilmiyorsa teslim de yoktur.'
  );
}

function hardwareConsequence(disposition: HardwareDisposition): string {
  switch (disposition) {
    case 'SOLD':
      return 'Mülkiyet geçiyor → mal teslimi. AB B2B: kutu 46, IC kodu L. Yalnız MAL kaleminde seçilebilir.';
    case 'RENTED':
      return 'Abonelik süresince kullandırılıp iade ediliyor → hizmet. AB B2B: kutu 44, IC kodu S.';
    case 'NA':
      return 'Kalem donanım içermiyor.';
  }
}

// ──────────────────────────────────────────────────────────── form: kip

function ItemFormModal({
  mode,
  item,
  onClose,
}: {
  mode: FormMode;
  item?: Item;
  onClose: () => void;
}) {
  const readOnly = mode === 'view';

  const [code, setCode] = useState(item?.code ?? '');
  const [names, setNames] = useState<LanguageValues>(() => toLanguageValues(item?.name ?? null));
  const [descriptions, setDescriptions] = useState<LanguageValues>(() =>
    toLanguageValues(item?.description ?? null),
  );
  const [unit, setUnit] = useState<ItemUnit>(item?.unit ?? 'PIECE');
  // ⚠ Sunucudan gelen METİN olduğu gibi tutulur ('1234.56'). Biçimlemek için
  // sayıya çevirmek, kaydetmede geri gönderilecek değeri bozardı.
  const [unitPrice, setUnitPrice] = useState(item?.unitPrice ?? '');
  const [vatRate, setVatRate] = useState(item ? trimRateText(item.vatRate) : '21');
  const [supplyKind, setSupplyKind] = useState<SupplyKind>(item?.supplyKind ?? 'SERVICE');
  const [isEss, setIsEss] = useState(item?.isEss ?? false);
  const [isImmovableWork, setIsImmovableWork] = useState(item?.isImmovableWork ?? false);
  const [hardwareDisposition, setHardwareDisposition] = useState<HardwareDisposition>(
    item?.hardwareDisposition ?? 'NA',
  );
  const [revenueAccountCode, setRevenueAccountCode] = useState(item?.revenueAccountCode ?? '');
  const [months, setMonths] = useState(
    item?.defaultServicePeriodMonths === null || item?.defaultServicePeriodMonths === undefined
      ? ''
      : String(item.defaultServicePeriodMonths),
  );
  const [active, setActive] = useState(item?.active ?? true);

  /** Alan → alan altına yazılacak kırmızı metin. Yerel denetim ve sunucu ortak kullanır. */
  const [problems, setProblems] = useState<Map<string, string>>(new Map());
  const [formMessage, setFormMessage] = useState<string | null>(null);

  const create = useCreateItem();
  const update = useUpdateItem();
  const saving = create.isPending || update.isPending;

  const violation = crossRuleViolation({
    supplyKind,
    isEss,
    isImmovableWork,
    hardwareDisposition,
  });

  function problemFor(field: string): string | null {
    return problems.get(field) ?? null;
  }

  function fail(message: string, fields: Array<[string, string]>): void {
    setFormMessage(message);
    setProblems(new Map(fields));
  }

  function onServerError(err: unknown): void {
    setProblems(itemFieldProblems(err));
    setFormMessage(itemErrorMessage(err));
    toastItemError(err);
  }

  /** Sunucuya gitmeden yakalanabilecekler. Asıl kapı yine sunucudadır. */
  function validate(): { name: ItemText; description: ItemText | null } | null {
    const name = buildItemText(names);
    if (name === null) {
      fail('Fransızca ad zorunlu.', [
        ['name.fr', 'Fatura Fransızca düzenlenir; Fransızca adı olmayan kalem faturaya basılamaz.'],
      ]);
      return null;
    }

    const description = buildItemText(descriptions);
    if (description === null && hasAnyText(descriptions)) {
      fail('Açıklama girdiyseniz Fransızcası zorunlu.', [
        ['description.fr', 'Faturaya basılan metnin dili Fransızcadır.'],
      ]);
      return null;
    }

    const price = unitPrice.trim();
    if (price !== '' && !MONEY_PATTERN.test(price)) {
      fail('Birim fiyat geçersiz.', [
        ['unitPrice', 'Nokta ile ve en fazla iki ondalık yazın (örn. 1234.56) — virgül değil.'],
      ]);
      return null;
    }

    const period = months.trim();
    if (period !== '' && !MONTHS_PATTERN.test(period)) {
      fail('Varsayılan hizmet dönemi geçersiz.', [
        ['defaultServicePeriodMonths', '1 ile 120 arasında tam sayı ay.'],
      ]);
      return null;
    }

    /*
     * ⚠ ÇAPRAZ KURAL BURADA OTOMATİK DÜZELTİLMEZ. "MAL seçildi, o hâlde
     * elektronik hizmet işaretini kaldırayım" demek, operatörün hiç görmediği
     * bir KDV kararını onun adına vermektir. Sunucu da aynı gerekçeyle
     * düzeltmiyor; panel yalnız aynı cümleyi bir tur erken söylüyor.
     */
    if (violation !== null) {
      fail(
        violation.message,
        violation.fields.map((field) => [field, violation.message] as [string, string]),
      );
      return null;
    }

    setProblems(new Map());
    setFormMessage(null);
    return { name, description };
  }

  function submit(): void {
    const validated = validate();
    if (validated === null) return;

    const trimmedCode = code.trim();
    const trimmedAccount = revenueAccountCode.trim();
    const trimmedPrice = unitPrice.trim();
    const trimmedMonths = months.trim();

    if (mode === 'create') {
      const body: CreateItemBody = {
        name: validated.name,
        unit,
        vatRate,
        supplyKind,
        isEss,
        isImmovableWork,
        hardwareDisposition,
        // Boş bırakılan opsiyoneller gövdeye HİÇ konmaz: sunucu varsayılanı
        // uygular, `null` göndermek "temizle" anlamına gelirdi.
        ...(trimmedCode !== '' ? { code: trimmedCode } : {}),
        ...(trimmedPrice !== '' ? { unitPrice: trimmedPrice } : {}),
        ...(validated.description !== null ? { description: validated.description } : {}),
        ...(trimmedAccount !== '' ? { revenueAccountCode: trimmedAccount } : {}),
        ...(trimmedMonths !== '' ? { defaultServicePeriodMonths: trimmedMonths } : {}),
      };
      create.mutate(body, {
        onSuccess: () => {
          toast.success('Kalem oluşturuldu.');
          onClose();
        },
        onError: onServerError,
      });
      return;
    }

    if (!item) return;

    /*
     * YALNIZ DEĞİŞENLER gönderilir. Denetim kaydı güncellemede değişen alan
     * ADLARINI yazıyor (`ITEM_UPDATED.details.keys`) ve o kayıt SALT-EKLEMEDİR:
     * dokunulmamış alanı da göndermek, üç ay sonra "bu kalem ne zaman
     * GOODS'tan SERVICE'e döndü" sorusunun cevabını gürültüye boğardı.
     */
    const patch: ItemWriteBody = {};
    const nextCode = trimmedCode === '' ? null : trimmedCode;
    if (nextCode !== item.code) patch.code = nextCode;
    if (JSON.stringify(validated.name) !== JSON.stringify(item.name)) patch.name = validated.name;
    if (JSON.stringify(validated.description) !== JSON.stringify(item.description)) {
      patch.description = validated.description;
    }
    if (unit !== item.unit) patch.unit = unit;
    if (trimmedPrice !== '' && trimmedPrice !== item.unitPrice) patch.unitPrice = trimmedPrice;
    if (vatRate !== trimRateText(item.vatRate)) patch.vatRate = vatRate;
    if (supplyKind !== item.supplyKind) patch.supplyKind = supplyKind;
    if (isEss !== item.isEss) patch.isEss = isEss;
    if (isImmovableWork !== item.isImmovableWork) patch.isImmovableWork = isImmovableWork;
    if (hardwareDisposition !== item.hardwareDisposition) {
      patch.hardwareDisposition = hardwareDisposition;
    }
    const nextAccount = trimmedAccount === '' ? null : trimmedAccount;
    if (nextAccount !== item.revenueAccountCode) patch.revenueAccountCode = nextAccount;
    const currentMonths =
      item.defaultServicePeriodMonths === null ? '' : String(item.defaultServicePeriodMonths);
    if (trimmedMonths !== currentMonths) {
      patch.defaultServicePeriodMonths = trimmedMonths === '' ? null : trimmedMonths;
    }
    if (active !== item.active) patch.active = active;

    if (Object.keys(patch).length === 0) {
      toast.info('Değişiklik yok.');
      onClose();
      return;
    }

    update.mutate(
      { id: item.id, body: patch },
      {
        onSuccess: () => {
          toast.success('Kalem güncellendi.');
          onClose();
        },
        onError: onServerError,
      },
    );
  }

  const title = mode === 'create' ? 'Yeni kalem' : (item?.name.fr ?? 'Kalem');

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        readOnly ? (
          <Button variant="secondary" onClick={onClose}>
            Kapat
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Vazgeç
            </Button>
            <Button loading={saving} onClick={submit}>
              {mode === 'create' ? 'Oluştur' : 'Kaydet'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {formMessage !== null && (
          <p className="rounded-[var(--r-md)] bg-[var(--c-danger-soft)] px-3 py-2 text-xs text-[var(--c-danger)]">
            {formMessage}
          </p>
        )}

        <Input
          label="Kod"
          placeholder="KIOSK-SUB"
          value={code}
          disabled={readOnly}
          error={problemFor('code')}
          hint="İsteğe bağlı, benzersiz. Katalogda aramayı hızlandırır."
          onChange={(e) => setCode(e.target.value)}
        />

        <MultilingualField
          legend="Ad"
          values={names}
          onChange={setNames}
          disabled={readOnly}
          fieldPrefix="name"
          problemFor={problemFor}
          requiredNote="Fransızca ad ZORUNLU: işletme merkezi Charleroi → Valonya → Taaldecreet 19.07.1973 uyarınca fatura Fransızca düzenlenir. Yaptırım hükümsüzlüktür ve hâkim resen dikkate alır."
          secondaryNote="Türkçe ad faturaya ASLA basılmaz; yalnız bu panelde aramak ve tanımak için."
        />

        <MultilingualField
          legend="Açıklama"
          values={descriptions}
          onChange={setDescriptions}
          disabled={readOnly}
          fieldPrefix="description"
          problemFor={problemFor}
          multiline
          requiredNote="İsteğe bağlı. Ama doldurulacaksa Fransızcası zorunlu — faturaya basılan metin odur."
          secondaryNote="Fatura satırında ayrıca yazılmazsa bu metin kullanılır."
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            label="Birim"
            value={unit}
            disabled={readOnly}
            onChange={(e) => setUnit(e.target.value as ItemUnit)}
          >
            {ITEM_UNITS.map((value) => (
              <option key={value} value={value}>
                {UNIT_LABELS[value]}
              </option>
            ))}
          </Select>

          <Input
            label="Birim fiyat (KDV hariç)"
            inputMode="decimal"
            placeholder="1234.56"
            value={unitPrice}
            disabled={readOnly}
            error={problemFor('unitPrice')}
            hint="Nokta ile yazın."
            onChange={(e) => setUnitPrice(e.target.value)}
          />

          <Select
            label="KDV oranı"
            value={vatRate}
            disabled={readOnly}
            error={problemFor('vatRate')}
            onChange={(e) => setVatRate(e.target.value)}
          >
            {VAT_RATE_CHOICES.map((rate) => (
              <option key={rate} value={rate}>
                %{rate}
              </option>
            ))}
            {/* Kayıtlı oran listede yoksa onu KAYBETMEYELİM: seçim sessizce
                %21'e kaymasın. */}
            {!VAT_RATE_CHOICES.includes(vatRate as (typeof VAT_RATE_CHOICES)[number]) && (
              <option value={vatRate}>%{vatRate}</option>
            )}
          </Select>
        </div>
        <p className="-mt-2 text-xs text-[var(--c-ink-faint)]">
          Oran yalnızca VARSAYILANDIR: ters yükümlülük ya da muafiyet çıkan satırda çözümleyici
          oranı zaten sıfırlar.
        </p>

        <VatQualification
          supplyKind={supplyKind}
          isEss={isEss}
          isImmovableWork={isImmovableWork}
          hardwareDisposition={hardwareDisposition}
          disabled={readOnly}
          violationMessage={violation?.message ?? null}
          problemFor={problemFor}
          onSupplyKind={setSupplyKind}
          onEss={setIsEss}
          onImmovableWork={setIsImmovableWork}
          onHardware={setHardwareDisposition}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Gelir hesabı (MAR/PCMN)"
            placeholder="700000"
            maxLength={10}
            value={revenueAccountCode}
            disabled={readOnly}
            error={problemFor('revenueAccountCode')}
            hint="İsteğe bağlı. Fatura satırının gelir hesabı buradan gelir."
            onChange={(e) => setRevenueAccountCode(e.target.value)}
          />
          <Input
            label="Varsayılan hizmet dönemi (ay)"
            inputMode="numeric"
            placeholder="12"
            value={months}
            disabled={readOnly}
            error={problemFor('defaultServicePeriodMonths')}
            hint="Abonelik şablonu; fatura satırındaki dönem alanlarını önceden doldurur."
            onChange={(e) => setMonths(e.target.value)}
          />
        </div>

        {mode !== 'create' && (
          <Checkbox
            label="Kalem aktif"
            checked={active}
            disabled={readOnly}
            hint="Pasif kalem yeni faturalarda seçilemez; geçmiş faturaların satırları olduğu gibi kalır. Faturada kullanılmış kalem silinemez — emeklilik yolu budur."
            onChange={(e) => setActive(e.target.checked)}
          />
        )}

        {readOnly && (
          <p className="text-xs text-[var(--c-ink-faint)]">
            Salt okuma yetkisiyle görüntülüyorsunuz; değişiklik yapılamaz.
          </p>
        )}
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────────── çok dilli metin alanı

function MultilingualField({
  legend,
  values,
  onChange,
  disabled,
  fieldPrefix,
  problemFor,
  requiredNote,
  secondaryNote,
  multiline = false,
}: {
  legend: string;
  values: LanguageValues;
  onChange: (next: LanguageValues) => void;
  disabled: boolean;
  fieldPrefix: 'name' | 'description';
  problemFor: (field: string) => string | null;
  requiredNote: string;
  secondaryNote: string;
  multiline?: boolean;
}) {
  /*
   * ⚠ AÇILIŞ DURUMU BİR KEZ hesaplanır. Her render'da türetilseydi, dolu tek
   * ikincil dili temizleyen kullanıcının altından panel kapanırdı — kendi
   * sildiği alanı geri yazmak isterse yeniden açmak zorunda kalırdı.
   */
  const [secondaryOpen] = useState(() =>
    ITEM_SECONDARY_LANGUAGES.some((lang) => values[lang] !== ''),
  );
  const Field = multiline ? Textarea : Input;

  function set(lang: 'fr' | ItemSecondaryLanguage, value: string) {
    onChange({ ...values, [lang]: value });
  }

  return (
    <fieldset className="rounded-[var(--r-md)] border border-[var(--c-border)] px-3 py-3">
      <legend className="px-1 text-[13px] font-medium text-[var(--c-ink)]">{legend}</legend>

      <Field
        label="Fransızca (faturaya basılan dil)"
        value={values.fr}
        disabled={disabled}
        error={
          problemFor(`${fieldPrefix}.fr`) ??
          // Sunucu `assertItemText` hatasında yalnız kök alanı suçluyor
          // (`fields: ['name']`); kırmızıyı Fransızca kutuya taşıyoruz.
          problemFor(fieldPrefix)
        }
        hint={requiredNote}
        onChange={(e) => set('fr', e.target.value)}
      />

      <details open={secondaryOpen} className="mt-2">
        <summary className="cursor-pointer text-xs text-[var(--c-ink-soft)]">
          Diğer diller (isteğe bağlı)
        </summary>
        <div className="mt-2 space-y-2">
          <p className="text-xs text-[var(--c-ink-faint)]">{secondaryNote}</p>
          {ITEM_SECONDARY_LANGUAGES.map((lang) => (
            <Field
              key={lang}
              label={ITEM_TEXT_LANGUAGE_LABELS[lang]}
              value={values[lang]}
              disabled={disabled}
              error={problemFor(`${fieldPrefix}.${lang}`)}
              onChange={(e) => set(lang, e.target.value)}
            />
          ))}
          <p className="text-xs text-[var(--c-ink-faint)]">
            Boş bırakılan dil kaydedilmez; dolu bir dili boşaltmak onu siler.
          </p>
        </div>
      </details>
    </fieldset>
  );
}

// ─────────────────────────────────────────────────────── dört KDV alanı

function VatQualification({
  supplyKind,
  isEss,
  isImmovableWork,
  hardwareDisposition,
  disabled,
  violationMessage,
  problemFor,
  onSupplyKind,
  onEss,
  onImmovableWork,
  onHardware,
}: {
  supplyKind: SupplyKind;
  isEss: boolean;
  isImmovableWork: boolean;
  hardwareDisposition: HardwareDisposition;
  disabled: boolean;
  violationMessage: string | null;
  problemFor: (field: string) => string | null;
  onSupplyKind: (value: SupplyKind) => void;
  onEss: (value: boolean) => void;
  onImmovableWork: (value: boolean) => void;
  onHardware: (value: HardwareDisposition) => void;
}) {
  return (
    <fieldset className="rounded-[var(--r-md)] border border-[var(--c-brand)] px-3 py-3">
      <legend className="px-1 text-[13px] font-semibold text-[var(--c-brand)]">
        KDV nitelendirmesi
      </legend>
      <p className="mb-3 text-xs text-[var(--c-ink-soft)]">
        Bu dört alan faturanın hangi Intervat kutusuna ve hangi IC listing koduna düşeceğini
        belirler. Yanlış değer anında görünmez: hata, beyan reddedildiğinde ya da denetimde ortaya
        çıkar.
      </p>

      {violationMessage !== null && (
        <p className="mb-3 rounded-[var(--r-md)] bg-[var(--c-danger-soft)] px-3 py-2 text-xs text-[var(--c-danger)]">
          {violationMessage}
        </p>
      )}

      <div className="space-y-3">
        <Select
          label="Tür"
          value={supplyKind}
          disabled={disabled}
          error={problemFor('supplyKind')}
          hint={supplyKindConsequence(supplyKind)}
          onChange={(e) => onSupplyKind(e.target.value as SupplyKind)}
        >
          {SUPPLY_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {SUPPLY_KIND_LABELS[kind]}
            </option>
          ))}
        </Select>

        <div>
          <Checkbox
            label="Elektronik hizmet (otomatik)"
            checked={isEss}
            disabled={disabled}
            hint="Reg. 282/2011 md. 7: internet üzerinden, ESASEN OTOMATİK, asgari insan müdahalesi. Otomatik SaaS aboneliği EVET; insan emeğiyle yapılan restoran web sitesi HAYIR."
            onChange={(e) => onEss(e.target.checked)}
          />
          {/*
           * ⚠ İKİ DÜZELTME. (1) Varış ülkesi oranı KENDİLİĞİNDEN gelmez:
           * Dir. md. 59c'nin 10.000 €'luk AB geneli B2C eşiği aşılmadıkça —
           * ya da OSS'ye gönüllü geçilmedikçe — satış Belçika %21'i kalır.
           * (2) M1'de motor bu satışı HER HÂLÜKÂRDA yurtiçine (kutu 3) yazar;
           * OSS dalı ancak cari kaydında açıkça seçildiğinde devreye girer.
           * Koşulsuz "kutu 47" yazmak, olmayan bir davranışı vaat etmekti.
           */}
          <FieldNote error={problemFor('isEss')}>
            {isEss
              ? "AB'li tüketiciye varış ülkesi oranı ve OSS (kutu 47) YALNIZCA AB geneli B2C " +
                "cironuz cari ya da önceki yıl 10.000 €'yu aştıysa veya OSS'ye gönüllü " +
                'geçtiyseniz uygulanır (Dir. md. 59c). Eşiğin altında satış Belçika %21’i ' +
                'kalır. M1 bu kalemi varsayılan olarak yurtiçine (kutu 3) yazar; OSS dalı ' +
                'cari kaydından açıkça seçilir.'
              : "AB'li tüketiciye de Belçika %21'i uygulanır; satış OSS'ye girmez."}
          </FieldNote>
        </div>

        <div>
          <Checkbox
            label="Taşınmaz üzerinde iş"
            checked={isImmovableWork}
            disabled={disabled}
            hint="WBTW md. 19 §2. Kiosk taşınmaza SABİTLENİYORSA bu iş odur."
            onChange={(e) => onImmovableWork(e.target.checked)}
          />
          <FieldNote error={problemFor('isImmovableWork')}>
            {isImmovableWork
              ? 'Belçikalı, periyodik beyan veren B2B müşteride yurtiçi ters yükümlülük: kutu 3/54 yerine kutu 45, faturada KDV gösterilmez ve zorunlu ibare eklenir.'
              : 'Yurtiçi B2B satışta normal %21 uygulanır (kutu 3/54).'}
          </FieldNote>
          {/* Kullanıcı kararı (2026-09-15): bugün donanım tedarik edilmiyor. Bu
              işaret ve "Donanımın akıbeti" sunucuda `hardware_supply_enabled`
              kapısına bağlı. Panel anahtarı OKUMAZ — muhasebeci rolü ayar alanını
              göremez ve yalnız-admin anahtarlar ona gizlidir; tek kaynak sunucu,
              ret de alanın altında görünür. */}
          <p className="mt-1 rounded-[var(--r-md)] bg-[var(--c-warn-soft)] px-3 py-2 text-xs text-[var(--c-warn)]">
            Emin değilseniz HAYIR bırakın: yanlış EVET, tahsil edilmesi gereken KDV'yi hiç tahsil
            etmemek demektir ve fark mükellefin cebinden çıkar. Serbest duran fişli kiosk büyük
            olasılıkla taşınır maldır. Kiosk'u taşınmaza sabitliyorsanız ruling alın.
          </p>
          <p className="mt-1 text-xs text-[var(--c-ink-faint)]">
            Donanım tedariki şirket ayarlarında kapalıysa bu işaret ve donanım satışı/kiralaması
            kaydedilmez. Açmak yöneticinin kararıdır: Ayarlar → Vergi profili.
          </p>
        </div>

        <Select
          label="Donanımın akıbeti"
          value={hardwareDisposition}
          disabled={disabled}
          error={problemFor('hardwareDisposition')}
          hint={hardwareConsequence(hardwareDisposition)}
          onChange={(e) => onHardware(e.target.value as HardwareDisposition)}
        >
          {HARDWARE_DISPOSITIONS.map((value) => (
            <option key={value} value={value}>
              {HARDWARE_DISPOSITION_LABELS[value]}
            </option>
          ))}
        </Select>
        <p className="text-xs text-[var(--c-ink-faint)]">
          CJEU CPP (C-349/96) ve Levob (C-41/04): aynı kiosk, sözleşmeye göre iki AYRI kutuya gider.
        </p>
      </div>
    </fieldset>
  );
}

function FieldNote({ error, children }: { error: string | null; children: ReactNode }) {
  if (error !== null) {
    return <p className="ml-6 mt-1 text-xs text-[var(--c-danger)]">{error}</p>;
  }
  return <p className="ml-6 mt-1 text-xs text-[var(--c-ink-soft)]">{children}</p>;
}

// ──────────────────────────────────────────────────────────────── silme

function DeleteItemModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const remove = useDeleteItem();
  const update = useUpdateItem();
  const [blocked, setBlocked] = useState<string | null>(null);

  return (
    <Modal
      open
      onClose={onClose}
      title="Kalemi sil"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            variant="secondary"
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                { id: item.id, body: { active: false } },
                {
                  onSuccess: () => {
                    toast.success('Kalem pasifleştirildi.');
                    onClose();
                  },
                  onError: toastItemError,
                },
              )
            }
          >
            Pasifleştir
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() =>
              remove.mutate(item.id, {
                onSuccess: () => {
                  toast.success('Kalem silindi.');
                  onClose();
                },
                onError: (err) => {
                  // Faturada kullanılmış kalem SİLİNEMEZ (FK RESTRICT). Kipi
                  // kapatmıyoruz: çıkış yolu (pasifleştirme) hemen yanında.
                  setBlocked(itemErrorMessage(err));
                  toastItemError(err);
                },
              })
            }
          >
            Sil
          </Button>
        </>
      }
    >
      <p className="text-sm">
        <b>{item.name.fr}</b> kalemi silinsin mi?
      </p>
      <p className="mt-2 text-xs text-[var(--c-ink-soft)]">
        Faturada kullanılmış kalem silinemez — mali bir belgenin gösterdiği hiçbir satır kaybolmaz.
        Kullanımdaki bir kalemi emekliye ayırmanın yolu pasifleştirmektir.
      </p>
      {blocked !== null && (
        <p className="mt-3 rounded-[var(--r-md)] bg-[var(--c-danger-soft)] px-3 py-2 text-xs text-[var(--c-danger)]">
          {blocked}
        </p>
      )}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── yardımcı

/**
 * Girdi yumuşatma — veri çekme DEĞİL: sorgunun kendisi TanStack Query'de.
 * Arama kutusuna her harfte istek atmak, 25 satırlık bir katalog için
 * gereksiz ve sonuçları sıra dışı döndürebilir.
 */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}
